import { useEffect, useRef } from 'react'
import { desenharAnimacoes, type Ancora } from './animacoesBlocos'
import { itemDoCatalogo, medidas, type Movel, type Tabuleiro } from './engine'
import { manifestoDosMoveis, spriteDoMovel } from './moveisSprites'
import { pintarChao, prepararPlanta, type PecaPronta } from './kitConstrucao'
import type { Planta } from './planta'
import { carregarLogo, chaveDaPaleta, desenharLogo, recolorida, type LugarDaLogo, type Paleta } from './paleta'

/**
 * O andar desenhado com imagens do Blender, na mesma vista do tabuleiro (SPEC-20261010-1914):
 * o visual realista e o pixel 2.0 de blocos (SPEC-20261010-2133). Cada modelo de sala tem a sua
 * imagem (em cima e embaixo), então a sala troca de cara junto com o modelo escolhido; a casca é o
 * resto do andar. Ordem: salas de cima, casca (com buracos onde elas ficam), salas de baixo.
 * No pixel 2.0 há ainda uma camada por cima com o que se mexe (água, luz, telas, pingue-pongue).
 */

export type Conjunto = 'realista' | 'blocos'

const ESCALA = 3
const VERSAO: Record<Conjunto, number> = { realista: 2, blocos: 2 }
const base = (c: Conjunto) => `/escritorio/${c}`
/** Recorte de cada imagem de sala em px do tabuleiro, a partir do canto da vaga. */
const SALA_X = -8
const SALA_Y = -37

const cache = new Map<string, Promise<HTMLImageElement>>()
function carregar(src: string) {
  let p = cache.get(src)
  if (!p) {
    p = new Promise((ok, erro) => {
      const img = new Image()
      img.decoding = 'async'
      img.onload = () => ok(img)
      img.onerror = erro
      img.src = src
    })
    cache.set(src, p)
  }
  return p
}
type Ancoras = { casca: Record<string, Ancora[]>; salas: Record<string, Ancora[]> }
const ancorasCache = new Map<string, Promise<Ancoras>>()
function ancoras(c: Conjunto) {
  const src = `${base(c)}/ancoras.json?v=${VERSAO[c]}`
  let p = ancorasCache.get(src)
  if (!p) {
    p = fetch(src).then((r) => (r.ok ? (r.json() as Promise<Ancoras>) : { casca: {}, salas: {} }))
    ancorasCache.set(src, p)
  }
  return p
}

/** Onde vai a logo da empresa em cada casca (SPEC-20261011-1733). */
const logosCache = new Map<string, Promise<Record<string, LugarDaLogo[]>>>()
function lugaresDaLogo(c: Conjunto) {
  const src = `${base(c)}/mascaras/logos.json?v=${VERSAO_MASCARA}`
  let p = logosCache.get(src)
  if (!p) {
    p = fetch(src)
      .then((r) => (r.ok ? (r.json() as Promise<Record<string, LugarDaLogo[]>>) : {}))
      .catch(() => ({}))
    logosCache.set(src, p)
  }
  return p
}
const VERSAO_MASCARA = 1
const mascaraDaCasca = (c: Conjunto, casca: string) => `${base(c)}/mascaras/${casca.replace(/\.\w+$/, '')}.webp?v=${VERSAO_MASCARA}`
const mascaraDaSala = (c: Conjunto, chave: string) => `${base(c)}/mascaras/salas/${chave}.webp?v=${VERSAO_MASCARA}`

/** Foto em perspectiva da sala, para o alto do cartão (888 x 396). */
export const capaDaSala = (modelo: string) => `${base('realista')}/capas/${modelo}.webp?v=${VERSAO.realista}`

export const imagemDaSala = (modelo: string, fileira: 'cima' | 'baixo', c: Conjunto = 'realista') => `${base(c)}/salas/${modelo}-${fileira}.webp?v=${VERSAO[c]}`

type Props = {
  tabuleiro: Tabuleiro
  conjunto?: Conjunto
  /** Modelo de desenho de cada lote ocupado (sem a área central). */
  modelos: Map<number, string>
  largura: number
  altura: number
  /** Móveis postos no modo criativo, desenhados com as imagens do Blender (SPEC-20261010-2330). */
  moveis?: Movel[]
  /** Cores da empresa (SPEC-20261011-1733): paredes, estofados, tapetes e marca. */
  paleta?: Paleta | null
  /** Logo da empresa (url), desenhada na recepção e no anel do chão. */
  logo?: string | null
  /** Planta livre (SPEC-20261011-2023): o andar sai do kit de chão e paredes, não de uma casca. */
  planta?: Planta | null
}

const TAPETES = new Set(['tapete', 'tapete_redondo'])

export function AndarRealista({ tabuleiro: t, conjunto = 'realista', modelos, largura, altura, moveis = [], paleta = null, logo = null, planta = null }: Props) {
  const ref = useRef<HTMLCanvasElement>(null)
  const mexe = useRef<HTMLCanvasElement>(null)
  const chave = [...modelos.entries()].sort((a, b) => a[0] - b[0]).map(([l, m]) => `${l}:${m}`).join('|')
  const chavePaleta = chaveDaPaleta(paleta)
  const chaveMoveis = moveis.map((m) => `${m.id}:${m.kind}:${m.x}:${m.y}:${m.variant}:${m.color}`).join('|')

  useEffect(() => {
    const canvas = ref.current
    const casca = conjunto === 'blocos' ? t.blocos?.casca : t.realista?.casca
    if (!canvas || !casca) return
    let vivo = true
    let pedido = 0
    const { ox, oy } = medidas(t)
    const livre = t.id === 'livre' && !!planta
    const pedacos = (livre ? [] : t.vagas).map((v) => {
      const modelo = modelos.get(v.id) ?? 'vazia'
      return { v, chave: `${modelo}-${v.fileira}`, src: imagemDaSala(modelo, v.fileira, conjunto), dx: Math.round((ox + v.x0 * 16 + SALA_X) * ESCALA), dy: Math.round((oy + v.y0 * 13.5 + SALA_Y) * ESCALA) }
    })
    const anc = conjunto === 'blocos' ? ancoras('blocos') : Promise.resolve(null)
    const moveisProntos = manifestoDosMoveis().then(async (man) => {
      const lista = moveis
        .map((m) => ({ m, sp: spriteDoMovel(man, conjunto, m) }))
        .filter((x): x is { m: Movel; sp: NonNullable<typeof x.sp> } => !!x.sp)
        .sort((a, b) => Number(TAPETES.has(b.m.kind)) - Number(TAPETES.has(a.m.kind)) || a.m.y + itemDoCatalogo(a.m.kind).d - (b.m.y + itemDoCatalogo(b.m.kind).d))
      const imgs = await Promise.all(lista.map((x) => Promise.all(Array.from({ length: x.sp.quadros }, (_, q) => carregar(x.sp.src(q)).catch(() => null)))))
      return lista.map((x, i) => ({
        ...x,
        imgs: imgs[i] as (HTMLImageElement | null)[],
        dx: Math.round((ox + x.m.x * 16 + x.sp.s.x) * ESCALA),
        dy: Math.round((oy + x.m.y * 13.5 + x.sp.s.y) * ESCALA),
      }))
    })
    const nomeCasca = casca.replace(/\.\w+$/, '')
    const kit = livre && planta ? prepararPlanta(conjunto, planta, ox, oy) : Promise.resolve(null)
    const cores = livre
      ? Promise.resolve([document.createElement('canvas') as CanvasImageSource])
      : Promise.all([carregar(`${base(conjunto)}/${casca}?v=${VERSAO[conjunto]}`), ...pedacos.map((p) => carregar(p.src))]).then(([f, ...ss]) =>
      Promise.all([recolorida(f, mascaraDaCasca(conjunto, casca), paleta), ...ss.map((im, i) => recolorida(im, mascaraDaSala(conjunto, (pedacos[i] as (typeof pedacos)[number]).chave), paleta))]),
    )
    const marca = logo && !livre ? Promise.all([carregarLogo(logo), lugaresDaLogo(conjunto)]) : Promise.resolve([null, {}] as const)
    void Promise.all([anc, moveisProntos, marca, cores, kit])
      .then(([pontos, postos, [imgLogo, lugares], [fundo, ...imgs], pronta]) => {
        if (!vivo || !fundo) return
        canvas.width = largura * ESCALA
        canvas.height = altura * ESCALA
        const g = canvas.getContext('2d') as CanvasRenderingContext2D
        g.clearRect(0, 0, canvas.width, canvas.height)
        const sombraDoMovel = (p: (typeof postos)[number]) => {
          if (conjunto !== 'blocos' || TAPETES.has(p.m.kind)) return
          const it = itemDoCatalogo(p.m.kind)
          g.fillStyle = 'rgba(40,24,10,.2)'
          g.beginPath()
          g.ellipse((ox + (p.m.x + it.w / 2) * 16) * ESCALA, (oy + (p.m.y + it.d * 0.6) * 13.5) * ESCALA, it.w * 8.6 * ESCALA, it.d * 6.2 * ESCALA, 0, 0, Math.PI * 2)
          g.fill()
        }
        if (livre && planta) {
          // planta livre: chão, depois paredes e móveis juntos, de trás para a frente
          if (pronta) pintarChao(g, pronta, planta, ox, oy)
          type Coisa = { chave: number; desenhar: () => void }
          const coisas: Coisa[] = [
            ...(pronta?.paredes ?? []).map((w: PecaPronta) => ({ chave: w.chave, desenhar: () => g.drawImage(w.img, w.dx, w.dy) })),
            ...postos.flatMap((p) => {
              const img = p.imgs[0]
              if (p.sp.quadros > 1 || !img) return []
              const chave = TAPETES.has(p.m.kind) ? -1000 + p.m.y : p.m.y + itemDoCatalogo(p.m.kind).d
              return [{ chave, desenhar: () => (sombraDoMovel(p), g.drawImage(img, p.dx, p.dy)) }]
            }),
          ]
          coisas.sort((a, b) => a.chave - b.chave).forEach((c) => c.desenhar())
        }
        const sala = (i: number) => {
          const p = pedacos[i] as (typeof pedacos)[number]
          g.drawImage(imgs[i] as CanvasImageSource, p.dx, p.dy)
        }
        if (!livre) pedacos.forEach((p, i) => p.v.fileira === 'cima' && sala(i))
        if (!livre) g.drawImage(fundo, 0, 0, canvas.width, canvas.height)
        if (imgLogo) desenharLogo(g, imgLogo, (lugares as Record<string, LugarDaLogo[]>)[nomeCasca] ?? [])
        // móveis postos (os bichinhos vão na camada de cima, porque se mexem)
        for (const p of livre ? [] : postos) {
          if (p.sp.quadros > 1) continue
          const img = p.imgs[0]
          if (!img) continue
          sombraDoMovel(p)
          g.drawImage(img, p.dx, p.dy)
        }
        pedacos.forEach((p, i) => p.v.fileira === 'baixo' && sala(i))
        // o que se mexe
        const tela = mexe.current
        if (!tela) return
        const bichos = postos.filter((p) => p.sp.quadros > 1)
        const lista = [
          ...(pontos?.casca[casca.replace(/\.\w+$/, '')] ?? []).map((a) => ({ a, dx: 0, dy: 0 })),
          ...pedacos.flatMap((p) => (pontos?.salas[p.chave] ?? []).map((a) => ({ a, dx: p.dx, dy: p.dy }))),
          ...postos.flatMap((p) => (p.sp.s.anc ?? []).map((a) => ({ a, dx: p.dx, dy: p.dy }))),
        ]
        const desenhar = (m: CanvasRenderingContext2D, t: number, q: number, parado: boolean) => {
          m.clearRect(0, 0, tela.width, tela.height)
          bichos.forEach((b, i) => {
            const img = b.imgs[parado ? 0 : Math.floor(t * 3.2 + i * 1.7) % b.sp.quadros]
            if (img) m.drawImage(img, b.dx, b.dy)
          })
          desenharAnimacoes(m, lista, t, q, parado)
        }
        tela.width = canvas.width
        tela.height = canvas.height
        const m = tela.getContext('2d') as CanvasRenderingContext2D
        const parado = matchMedia('(prefers-reduced-motion: reduce)').matches
        let q = 0
        let ultimo = 0
        const passo = (ms: number) => {
          if (!vivo) return
          pedido = requestAnimationFrame(passo)
          if (ms - ultimo < 33) return // ~30 quadros por segundo bastam
          ultimo = ms
          q++
          desenhar(m, ms / 1000, q, false)
        }
        if (parado) desenhar(m, 0, 0, true)
        else pedido = requestAnimationFrame(passo)
      })
      .catch(() => {})
    return () => {
      vivo = false
      cancelAnimationFrame(pedido)
    }
    // chave resume os modelos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, chave, chaveMoveis, largura, altura, conjunto, chavePaleta, logo, planta])

  return (
    <>
      <canvas ref={ref} className="esc__realista" style={{ width: largura, height: altura, imageRendering: conjunto === 'blocos' ? 'pixelated' : 'auto' }} aria-hidden />
      <canvas ref={mexe} className="esc__realista" style={{ width: largura, height: altura }} aria-hidden />
    </>
  )
}
